import { ComponentType, ReactNode, SVGProps } from 'react'

import { cn } from '@/app/utils/style'
import DecorImage1Bottom from '@/public/images/decorImage1Bottom.svg'
import DecorImage1Top from '@/public/images/decorImage1Top.svg'
import DecorImage2Bottom from '@/public/images/decorImage2Bottom.svg'
import DecorImage2Top from '@/public/images/decorImage2Top.svg'
import DecorImage3Bottom from '@/public/images/decorImage3Bottom.svg'
import DecorImage3Top from '@/public/images/decorImage3Top.svg'
import DecorImage4Bottom from '@/public/images/decorImage4Bottom.svg'
import DecorImage4Top from '@/public/images/decorImage4Top.svg'
import type { ImageWithDecor as ImageWithDecorType } from '@/sanity.types'

type ImageWithDecorProps = {
  decor: ImageWithDecorType['decor']
  children: ReactNode
  className?: string
}

type DecorAlign = 'left' | 'right'

type DecorItem = {
  Component: ComponentType<SVGProps<SVGSVGElement>>
  align: DecorAlign
  width: number
  height: number
}

const decorConfig: Record<
  Exclude<ImageWithDecorType['decor'], 'none'>,
  { top: DecorItem; bottom: DecorItem }
> = {
  '1': {
    top: { Component: DecorImage1Top, align: 'right', width: 85, height: 85 },
    bottom: { Component: DecorImage1Bottom, align: 'left', width: 81, height: 81 },
  },
  '2': {
    top: { Component: DecorImage2Top, align: 'left', width: 90, height: 83 },
    bottom: { Component: DecorImage2Bottom, align: 'right', width: 66, height: 64 },
  },
  '3': {
    top: { Component: DecorImage3Top, align: 'left', width: 80, height: 78 },
    bottom: { Component: DecorImage3Bottom, align: 'right', width: 61, height: 65 },
  },
  '4': {
    top: { Component: DecorImage4Top, align: 'left', width: 78, height: 78 },
    bottom: { Component: DecorImage4Bottom, align: 'right', width: 73, height: 73 },
  },
}

const getPositionClasses = (align: DecorAlign) =>
  align === 'left'
    ? 'left-0 origin-left -translate-x-[16px] md:-translate-x-[24px] lg:-translate-x-[32px]'
    : 'right-0 origin-right translate-x-[16px] md:translate-x-[24px] lg:translate-x-[32px]'

const SHARED_CLASSES = 'absolute pointer-events-none z-10 scale-[0.53] md:scale-[0.68] lg:scale-100'

const renderDecor = (item: DecorItem, position: 'top' | 'bottom') => (
  <item.Component
    className={cn(
      SHARED_CLASSES,
      position === 'top' ? 'top-[10%]' : 'bottom-[10%]',
      getPositionClasses(item.align),
    )}
    width={item.width}
    height={item.height}
  />
)

export function ImageWithDecor({ decor, children, className }: ImageWithDecorProps) {
  const config = decor !== 'none' ? decorConfig[decor] : null

  return (
    <div className={cn('relative', className)}>
      {children}
      {config && (
        <>
          {renderDecor(config.top, 'top')}
          {renderDecor(config.bottom, 'bottom')}
        </>
      )}
    </div>
  )
}
